import { useState } from 'react';
import { TextField } from '@recargas-dominicanas/core/components';
import { BusinessUserApi, CustomerApi } from '@recargas-dominicanas/core/api';
import type { IBusinessUser } from '@recargas-dominicanas/core/types';
import { Modal } from '../../../../components/modal/modal.component';
import { BusinessStore } from '../../../../store/businessStore';
import { useForm } from '@recargas-dominicanas/core/hooks';
import { style } from './business-user-edit-modal.module.css';

interface Props {
  open: boolean;
  businessId: number;
  businessUser: IBusinessUser;
  onClose: () => void;
}

export function BusinessUserEditModal(props: Props) {

  const { open, businessId, businessUser, onClose } = props;

  const [error, setError] = useState<string>();

  const form = useForm({
    name: businessUser.name,
    userName: {
      value: businessUser.userName,
      minLength: {
        length: 4,
        error: 'El nombre de usuario debe tener mínimo 4 caracteres'
      },
      maxLength: {
        length: 30,
        error: 'El nombre de usuario debe tener máximo 30 caracteres'
      },
      username: true
    }
  }, [open, businessUser.name, businessUser.userName]);

  async function validateUserName() {

    if (!form.userName.validate()) return false;

    if (form.userName.value === businessUser.userName) return true;

    try {
      const result = await CustomerApi.usernameExists(form.userName.value);

      if (result.exists) {
        form.userName.setError('Nombre de usuario no disponible');
        return false;
      }
    } catch {
      form.userName.setError('Error validando nombre de usuario');
      return false;
    }

    return true;
  }

  function handleClose() {
    setError(undefined);
    form.clear();
    onClose();
  }

  async function handleSave() {

    if (!form.validate()) return;

    const isUserNameValid = await validateUserName();

    if (!isUserNameValid) return;

    if (
      form.name.value === businessUser.name &&
      form.userName.value === businessUser.userName
    ) {
      handleClose();
      return;
    }

    setError(undefined);

    try {
      await BusinessUserApi.update(businessUser.id, {
        name: form.name.value,
        userName: form.userName.value
      });
      await BusinessStore.fetchBusinessUsers(businessId);
      handleClose();
    } catch (requestError: any) {
      if (requestError.response?.status === 409) {
        form.userName.setError('Nombre de usuario no disponible');
        return;
      }

      setError('Ocurrió un error al actualizar el usuario');
    }
  }

  return (
    <Modal
      open={open}
      style={style.modal}
      title='Editar usuario'
      error={error}
      primaryButton='Guardar'
      onClose={handleClose}
      onPrimaryClick={handleSave}
      onSecondaryClick={handleClose}>
      <TextField
        style={style.textField}
        label='Nombre'
        autofocus={true}
        value={form.name.value}
        error={form.name.error}
        onChange={form.name.onChange}
        onBlur={form.name.onBlur}
        onEnter={handleSave}/>
      <TextField
        style={style.textField}
        label='Usuario'
        value={form.userName.value}
        error={form.userName.error}
        onChange={form.userName.onChange}
        onBlur={validateUserName}
        onEnter={handleSave}/>
    </Modal>
  );
}
